import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  LayoutDashboard, Key, BarChart3, TrendingUp, Settings,
  Menu, X, Zap, Users, LogOut, Clock, ChevronDown, ScrollText
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const NAV_ITEMS = [
  { name: 'Dashboard',     href: '/',              icon: LayoutDashboard },
  { name: 'API Keys',      href: '/api-keys',      icon: Key },
  { name: 'Usage',         href: '/usage',         icon: BarChart3 },
  { name: 'Usage History', href: '/usage-history', icon: Clock },
  { name: 'Analytics',     href: '/analytics',     icon: TrendingUp },
  { name: 'Logs',          href: '/logs',          icon: ScrollText },
  { name: 'Settings',      href: '/settings',      icon: Settings },
];

function NavLink({ item, active, onClick }) {
  return (
    <Link
      to={item.href}
      onClick={onClick}
      className={`group flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-150 ${
        active
          ? 'bg-indigo-600 text-white'
          : 'text-slate-400 hover:text-white hover:bg-white/10'
      }`}
    >
      <item.icon size={18} className={active ? 'text-white' : 'text-slate-500 group-hover:text-white'} />
      {item.name}
    </Link>
  );
}

const Layout = ({ children }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef(null);
  const location = useLocation();
  const { user, logout, isAdmin } = useAuth();

  const navigation = isAdmin ? [...NAV_ITEMS, { name: 'Users', href: '/users', icon: Users }] : NAV_ITEMS;
  const isActive = (path) => location.pathname === path;

  const pageTitles = {
    '/': 'Dashboard', '/api-keys': 'API Keys', '/usage': 'Usage',
    '/usage-history': 'Usage History', '/analytics': 'Analytics', '/logs': 'Logs',
    '/settings': 'Settings', '/users': 'User Management',
  };
  const pageTitle = pageTitles[location.pathname] || 'Overview';

  useEffect(() => {
    function handleClick(e) {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) setUserMenuOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const Sidebar = ({ onNav }) => (
    <div className="flex flex-col h-full" style={{ background: '#09090f' }}>
      <div className="flex items-center gap-3 px-5 py-6">
        <div className="w-8 h-8 rounded-xl bg-indigo-600 flex items-center justify-center flex-shrink-0" style={{ boxShadow: '0 0 20px rgba(99,102,241,0.4)' }}>
          <Zap size={16} className="text-white" />
        </div>
        <div>
          <span className="text-white font-bold text-base tracking-tight leading-none">NEXT LLM</span>
          <span className="block text-slate-500 text-xs font-medium tracking-widest uppercase mt-0.5">Keys</span>
        </div>
      </div>

      <nav className="flex-1 px-3 space-y-0.5 overflow-y-auto">
        <p className="px-3 pt-2 pb-1.5 text-xs font-semibold text-slate-600 uppercase tracking-widest">Menu</p>
        {navigation.map(item => (
          <NavLink key={item.href} item={item} active={isActive(item.href)} onClick={onNav} />
        ))}
      </nav>

      <div className="px-3 py-4 border-t border-white/5">
        <div
          className="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-white/5 cursor-pointer transition-colors group"
          onClick={() => logout()}
        >
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center flex-shrink-0">
            <span className="text-white text-xs font-bold">
              {(user?.username || 'U').charAt(0).toUpperCase()}
            </span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-white truncate">{user?.username}</p>
            <p className="text-xs text-slate-500 capitalize">{user?.role}</p>
          </div>
          <LogOut size={15} className="text-slate-600 group-hover:text-red-400 transition-colors flex-shrink-0" />
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {mobileOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setMobileOpen(false)} />
          <div className="absolute inset-y-0 left-0 w-64">
            <Sidebar onNav={() => setMobileOpen(false)} />
            <button onClick={() => setMobileOpen(false)}
              className="absolute top-5 right-4 text-slate-500 hover:text-white">
              <X size={20} />
            </button>
          </div>
        </div>
      )}

      <div className="hidden lg:flex lg:flex-col lg:w-64 lg:fixed lg:inset-y-0">
        <Sidebar onNav={() => {}} />
      </div>

      <div className="flex-1 lg:pl-64 flex flex-col min-h-screen">
        <header className="sticky top-0 z-40 flex items-center gap-4 h-16 bg-white/80 backdrop-blur border-b border-slate-100 px-6" style={{ boxShadow: '0 1px 0 0 rgba(0,0,0,0.04)' }}>
          <button type="button" className="lg:hidden text-slate-500 hover:text-slate-700" onClick={() => setMobileOpen(true)}>
            <Menu size={22} />
          </button>
          <div className="flex-1">
            <h1 className="text-lg font-semibold text-slate-900">{pageTitle}</h1>
          </div>
          <div className="relative" ref={userMenuRef}>
            <button onClick={() => setUserMenuOpen(!userMenuOpen)}
              className="flex items-center gap-2.5 pl-3 pr-2 py-1.5 rounded-xl hover:bg-slate-50 transition-colors border border-slate-200">
              <div className="w-7 h-7 rounded-full bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center flex-shrink-0">
                <span className="text-white text-xs font-bold">{(user?.username || 'U').charAt(0).toUpperCase()}</span>
              </div>
              <span className="text-sm font-medium text-slate-700 hidden sm:block">{user?.username}</span>
              <ChevronDown size={14} className="text-slate-400" />
            </button>
            {userMenuOpen && (
              <div className="absolute right-0 mt-2 w-44 bg-white rounded-xl shadow-lg ring-1 ring-slate-900/5 py-1.5 z-50">
                <div className="px-3 py-2 border-b border-slate-100 mb-1">
                  <p className="text-xs font-semibold text-slate-800">{user?.username}</p>
                  <p className="text-xs text-slate-400 capitalize">{user?.role}</p>
                </div>
                <button
                  onClick={() => { logout(); setUserMenuOpen(false); }}
                  className="flex items-center gap-2.5 px-3 py-2 text-sm text-red-500 hover:bg-red-50 rounded-lg transition-colors mx-1"
                  style={{ width: 'calc(100% - 8px)' }}
                >
                  <LogOut size={14} />
                  Sign out
                </button>
              </div>
            )}
          </div>
        </header>

        <main className="flex-1 p-6">
          <div className="max-w-7xl mx-auto animate-fade-in">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};

export default Layout;
